import { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AutocompleteComponent from '../../../components/layout/Autocomplete';
import useContracts from '../../../components/layout/ContractSelector/hook/useContracts';
import { useGetCuitPresentacionesSvccQuery } from '../../../services/carcinogenicApi';
import InfoAlert from '../../../assets/icons/infoAlert.png';
import CancelAlert from '../../../assets/icons/cancelAlert.png';
import BackdropComponent from '../../../components/Backdrop/BackdropComponent';

export type SVCCActionType = 'cancel' | 'consolidate' | 'modify';

interface SVCCContractActionDialogProps {
    open: boolean;
    type: SVCCActionType;
    onClose: () => void;
    onConfirm: (cuit: string) => Promise<void>;
    loading: boolean;
    cuitPreseleccionado?: string;
}

const SVCCContractActionDialog = ({
    open,
    type,
    onClose,
    onConfirm,
    loading,
    cuitPreseleccionado
}: SVCCContractActionDialogProps) => {
    const { t } = useTranslation('presentaciones');
    const [selectedContract, setSelectedContract] = useState<any>(null);
    const { rawContracts, isFetching } = useContracts();

    const contractOptions = rawContracts.map((c: any) => ({
        id: c.id,
        nombre: `${c.cuit} - ${c.nombre}`,
        cuit: c.cuit
    }));

    const title = t(`svccActions.${type}.title`);
    const message = t(`svccActions.${type}.message`);
    const icon = type === 'cancel' ? CancelAlert : InfoAlert;

    const cuitActivo = cuitPreseleccionado ?? selectedContract?.cuit;

    /**
     * Cancelar sin trámite abierto en la SRT devuelve un 400 ("no hay nada que
     * cancelar"): se deshabilita antes de dejar mandarlo. `idPresentacionSrt`
     * en null es exactamente eso — sin id no hay presentación que cancelar.
     *
     * Sólo se bloquea cuando la grilla CONOCE ese CUIT. Si no está en la lista
     * (otro período, otro organismo) no se sabe, así que no se bloquea y el
     * backend sigue siendo la guarda final.
     */
    const { data: cuitsSvcc } = useGetCuitPresentacionesSvccQuery(
        // Se pide sólo el CUIT en juego: la grilla pagina en el servidor, así
        // que traerla entera para buscar una fila ya no tiene sentido.
        { cuit: cuitActivo },
        { skip: type !== 'cancel' || !open || !cuitActivo }
    );
    const filaDelCuit = (cuitsSvcc?.data?.data ?? []).find(
        (fila: any) => fila.cuit === cuitActivo
    );
    const sinTramiteAbierto =
        type === 'cancel' && Boolean(filaDelCuit) && filaDelCuit.idPresentacionSrt == null;

    const handleConfirm = async () => {
        if (!cuitActivo) return;
        await onConfirm(cuitActivo);
        setSelectedContract(null);
    };

    const handleClose = () => {
        setSelectedContract(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : handleClose}
            fullWidth
            disableEscapeKeyDown
            sx={{
                textAlign: 'center',
                '& .MuiPaper-root': {
                    borderRadius: '8px',
                    minWidth: '440px',
                    padding: '30px 20px'
                },
                '& .MuiDialogActions-root': {
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '20px',
                    '& button': {
                        padding: '10px 20px',
                        borderRadius: '4px',
                        textTransform: 'none',
                        fontSize: '14px',
                        width: '100%'
                    }
                },
                '& .MuiDialogTitle-root': {
                    fontSize: '20px',
                    fontWeight: '700',
                    paddingBottom: '8px'
                }
            }}
        >
            <Box display="flex" justifyContent="center">
                <img src={icon} alt={type} style={{ height: 80, marginTop: 30 }} />
            </Box>

            <DialogTitle>{title}</DialogTitle>

            <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Typography
                    sx={{
                        padding: '0 14px',
                        lineHeight: '22px',
                        fontSize: '17px',
                        overflowWrap: 'break-word',
                        maxWidth: '540px'
                    }}
                >
                    {message}
                </Typography>
                <Box sx={{ width: '100%', textAlign: 'left' }}>
                    {cuitPreseleccionado ? (
                        <Typography variant="body2" sx={{ color: '#475569', pl: '.3rem' }}>
                            {`CUIT: ${cuitPreseleccionado}`}
                        </Typography>
                    ) : (
                        <>
                            <Typography variant="body2" sx={{ mb: 1, color: '#475569', pl: '.3rem' }}>
                                {t('svccActions.contractHelper')}
                            </Typography>
                            <AutocompleteComponent
                                name="contrato"
                                label={t('svccActions.contractLabel')}
                                value={selectedContract}
                                options={contractOptions}
                                templateLabel={(option: any) => option?.nombre || ''}
                                onChange={(value: any) => setSelectedContract(value || null)}
                                fullWidth
                                loading={isFetching}
                            />
                        </>
                    )}

                    {sinTramiteAbierto && (
                        <Typography
                            variant="body2"
                            data-testid="sin-tramite-abierto"
                            sx={{ mt: 1.5, color: '#b3261e', pl: '.3rem' }}
                        >
                            Este CUIT no tiene una presentación abierta en la SRT: no hay nada
                            que cancelar. Se puede iniciar la presentación de cero.
                        </Typography>
                    )}
                </Box>
            </DialogContent>

            <BackdropComponent loading={loading} zIndex={1500} />

            <DialogActions>
                <Box display="flex" flexDirection="row" gap={2.3} width="100%">
                    <Button variant="outlined" onClick={handleClose} disabled={loading}>
                        {t('svccActions.cancelButton')}
                    </Button>
                    <Button
                        variant="contained"
                        color={type === 'cancel' ? 'error' : 'primary'}
                        onClick={handleConfirm}
                        disabled={!cuitActivo || loading || sinTramiteAbierto}
                    >
                        {t('svccActions.confirmButton')}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default SVCCContractActionDialog;
